/* Product-page presentation. Commerce remains in the native product/bundle controllers. */
(() => {
  const root = document.querySelector('jive-product-form');
  if (!root) return;
  const track = root.querySelector('[data-gallery-track]');
  const counter = root.querySelector('[data-pdp-gallery-count]');
  if (track && counter) {
    const updateCounter = () => {
      const first = track.firstElementChild;
      const width = first.getBoundingClientRect().width + 14;
      const current = Math.max(0, Math.min(track.children.length - 1, Math.round(track.scrollLeft / width)));
      counter.textContent = `${current + 1} / ${track.children.length}`;
    };
    track.addEventListener('scroll', updateCounter, { passive:true });
    window.addEventListener('resize', updateCounter);
    updateCounter();
  }

  // Attach the MP4 only when its slide is visible. The thumbnail stays in place
  // until playback begins, including when motion preferences or browser policy stop it.
  const galleryVideos = [...root.querySelectorAll('[data-pdp-gallery-video]')];
  if (galleryVideos.length && 'IntersectionObserver' in window) {
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const states = new Map(galleryVideos.map(video => [video, { visible: false, loaded: false, pending: false, blocked: false, failed: false, abortRetries: 0 }]));
    const obstructed = () => document.hidden || document.documentElement.classList.contains('jive-cart-open');
    const canPlay = state => state.visible && !obstructed() && !reducedMotion.matches && !state.failed;
    const pauseVideo = video => {
      video.removeAttribute('data-playing');
      video.pause();
    };
    const updateVideos = () => {
      for (const [video, state] of states) {
        if (!canPlay(state)) {
          pauseVideo(video);
          continue;
        }
        if (state.pending || state.blocked || !video.paused) continue;
        if (!state.loaded) {
          const source = video.querySelector('source[data-src]');
          if (!source) continue;
          video.muted = true;
          video.defaultMuted = true;
          source.src = source.dataset.src;
          state.loaded = true;
          video.load();
        }
        state.pending = true;
        video.play().catch(error => {
          // A swipe can interrupt a pending play. Browser-policy rejections
          // keep the poster and retry only after the shopper returns to the slide.
          if (error.name === 'AbortError' && state.abortRetries < 1) state.abortRetries += 1;
          else state.blocked = true;
          video.removeAttribute('data-playing');
        }).finally(() => {
          state.pending = false;
          if (canPlay(state) && video.paused && !state.blocked) updateVideos();
        });
      }
    };
    for (const [video, state] of states) {
      video.addEventListener('pause', () => video.removeAttribute('data-playing'));
      video.addEventListener('play', () => {
        if (!canPlay(state)) pauseVideo(video);
      });
      video.addEventListener('playing', () => {
        state.abortRetries = 0;
        if (canPlay(state)) video.setAttribute('data-playing', '');
        else pauseVideo(video);
      });
      const handleMediaError = () => {
        // WebKit can report an empty deferred source before it is hydrated.
        if (!state.loaded) return;
        state.failed = true;
        pauseVideo(video);
      };
      video.addEventListener('error', handleMediaError);
      video.querySelector('source')?.addEventListener('error', handleMediaError);
    }
    const videoObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const state = states.get(entry.target);
        const visible = entry.isIntersecting && entry.intersectionRatio >= .6;
        if (visible && !state.visible) {
          state.blocked = false;
          state.abortRetries = 0;
        }
        state.visible = visible;
      }
      updateVideos();
    }, { threshold: [0, .6] });
    for (const video of galleryVideos) videoObserver.observe(video);
    document.addEventListener('visibilitychange', updateVideos);
    reducedMotion.addEventListener('change', updateVideos);
    new MutationObserver(updateVideos).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  const disclosure = document.querySelector('[data-pdp-label]');
  const labelBody = disclosure?.querySelector('[data-pdp-label-body]');
  if (disclosure && labelBody && typeof HTMLDialogElement !== 'undefined') {
    const dialog = document.createElement('dialog');
    dialog.className = 'jive-pdp-dialog';
    dialog.id = 'JiveNutritionDialog';
    dialog.setAttribute('aria-labelledby', 'JiveNutritionTitle');
    const header = document.createElement('div');
    header.className = 'jive-pdp-dialog__header';
    const title = document.createElement('h2');
    title.id = 'JiveNutritionTitle';
    title.textContent = 'Nutrition & ingredients';
    const close = document.createElement('button');
    close.className = 'jive-pdp-dialog__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close nutrition details');
    close.textContent = '×';
    header.append(title, close);
    const body = document.createElement('div');
    body.className = 'jive-pdp-dialog__body';
    body.append(labelBody);
    dialog.append(header, body);
    document.body.append(dialog);
    let trigger = null;
    let previousOverflow = '';
    const picker = labelBody.querySelector('[data-pdp-label-flavor]');
    const panels = [...labelBody.querySelectorAll('[data-pdp-label-panel]')];
    const selectPanel = () => {
      if (!picker) return;
      for (const panel of panels) panel.hidden = panel.dataset.pdpLabelPanel !== picker.value;
    };
    if (picker) {
      const wrapper = labelBody.querySelector('[data-pdp-label-picker]');
      if (wrapper) wrapper.hidden = false;
      picker.addEventListener('change', selectPanel);
      selectPanel();
    }
    const open = event => {
      event.preventDefault();
      trigger = event.currentTarget;
      if (picker) {
        const selectedFlavor = root.bundle?.lastFlavor || root.querySelector('.jive-product-main__image')?.alt || '';
        const match = [...picker.options].find(option => selectedFlavor.toLowerCase().includes(option.value.toLowerCase()));
        if (match) picker.value = match.value;
        selectPanel();
      }
      previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      dialog.scrollTop = 0;
      close.focus();
    };
    const openers = [disclosure.querySelector('summary'), ...document.querySelectorAll('[data-pdp-open-label], .jive-product-main__facts-link')];
    for (const opener of openers) {
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.setAttribute('aria-controls', dialog.id);
      opener.addEventListener('click', open);
    }
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex="0"]')].filter(element => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = previousOverflow;
      trigger?.focus({ preventScroll:true });
    });
  }

  const reviews = document.getElementById('product-reviews');
  if (reviews) {
    const compactReviews = () => {
      // Judge.me makes summary stars interactive after loading. A named group
      // preserves their label without hiding their controls inside an image role.
      for (const stars of reviews.querySelectorAll('.jdgm-rev-widg__summary-stars[role="img"]')) {
        if (stars.querySelector('a[href], button, [tabindex="0"]')) stars.setAttribute('role', 'group');
      }
      for (const gallery of reviews.querySelectorAll('.jdgm-gallery')) {
        gallery.setAttribute('tabindex', '0');
        gallery.setAttribute('role', 'region');
        const title = gallery.closest('.jdgm-gallery-wrapper')?.querySelector('.jdgm-gallery-title');
        gallery.setAttribute('aria-label', title?.textContent.trim() || reviews.querySelector('h2')?.textContent.trim() || '');
      }
      // Judge.me moves its histogram during initialization. Remove the empty
      // disclosure it can leave behind before wrapping the current histogram.
      for (const stale of reviews.querySelectorAll('.jive-pdp-review-breakdown')) {
        if (!stale.querySelector('.jdgm-histogram')) stale.remove();
      }
      const histogram = reviews.querySelector('.jdgm-histogram');
      if (!histogram || histogram.closest('.jive-pdp-review-breakdown')) return;
      const details = document.createElement('details');
      details.className = 'jive-pdp-review-breakdown';
      const summary = document.createElement('summary');
      summary.textContent = 'See the rating breakdown';
      details.append(summary);
      histogram.before(details);
      details.append(histogram);
    };
    new MutationObserver(compactReviews).observe(reviews, { childList:true, subtree:true });
    compactReviews();
  }
})();
